import { clsx } from "clsx";
import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import Timeline from "ui/playback/timeline";
import { SvgCloudTransfer, SvgLocalTransfer, SvgMapMarker } from "ui/svgs";
import { STREAM_TYPE_PLAYBACK } from "webrtc-player/common/base";
import usePlayBackRtcService from "pages/dashboard/playback/service";
import { WsResDataType } from "lib/types";
import { showToast } from "ui/components/common";
import { UsingCameraPlayer } from "ui/streaming/single-pc-adapter";
let progressContinueMark = 0;
export const Playback = forwardRef(function Playback(props: any, ref: any) {
  const {
    refPlayerContainer,
    streamingCameraPlayback,
    refCameraPlayer = null,
    onStreamClosed,
    audioMuted,
    streamId,
    startAtTimeline,
    endAtTimeline,
    pause,
    onPauseChange,
    timelineInterval,
    setStartAtTimeline,
    setEndAtTimeline,
    disableAlertLine,
    disableSwitchArrow,
    firsRecordedDate,
    onProgressTime,
    streamEnd,
  } = props;
  const startAtTimelineRef = useRef(0);

  const [seekAt, setSeekAt] = useState<number>(-1);
  const { sendGetPlaybackRecordsRequest } = usePlayBackRtcService();
  const [progressTime, setProgressTime] = useState(0);

  const [streamStart, setStreamStart] = useState<number>(-1);
  const [transferType, setTransferType] = useState("");
  const handleSeekAt = (time: number) => {
    setTimeout(() => setSeekAt(() => time), 200);
  };

  const handleSeekOffset = (offset: number) => {
    setSeekAt(Math.round(progressTime * 1000) + offset * 1000);
  };

  useImperativeHandle(ref, () => ({
    handleSeekAt,
    handleSeekOffset,
    progressTime,
  }));

  useEffect(() => {
    if (!firsRecordedDate || !streamingCameraPlayback) {
      return;
    }

    const getPlaybackRecords = async (nextDay?: boolean) => {
      try {
        const res = await sendGetPlaybackRecordsRequest(
          streamingCameraPlayback?.cameraId,
          streamId,
          firsRecordedDate?.getTime() / 1000,
          firsRecordedDate?.getTime() / 1000 + 24 * 60 * 60 - 1,
          streamingCameraPlayback?.orgId,
        );
        const data = (res as WsResDataType)?.playbackGetRecordsReply?.records;
        if (data?.length === 0) {
          if (!nextDay) {
            getPlaybackRecords(true);
          } else {
            showToast(1, "There is no recording in first day.");
          }
          return;
        }
        console.log("wsMsgData records ", data);
        setStreamStart(data[0]?.startedAt);
      } catch (error) {
        showToast(
          1,
          "There was a problem searching the history records. Please try again. Error: " +
            error?.message,
        );
      }
    };
    getPlaybackRecords();
  }, [firsRecordedDate, streamId, streamingCameraPlayback]);

  const handleProgressTime = (currentOffset: number) => {
    const progressTime = currentOffset + streamStart;
    setProgressTime(progressTime);
    onProgressTime?.(progressTime);
  };

  useEffect(() => {
    setSeekAt(startAtTimeline);
    startAtTimelineRef.current = startAtTimeline;
  }, [startAtTimeline]);
  const onStreamOpen = useCallback(() => {
    progressContinueMark && setSeekAt(progressContinueMark);
  }, []);
  useEffect(() => {
    if (progressTime > 0) progressContinueMark = progressTime * 1000;
  }, [streamId]);

  return (
    <>
      <div
        className="flex-grow overflow-y-hidden flex items-center"
        ref={refPlayerContainer}
        id="playerContainer"
      >
        {streamingCameraPlayback ? (
          <div className="w-full1 h-full max-h-full max-w-full mx-auto aspect-video flex items-center justify-center">
            <div className="w-full aspect-video">
              <UsingCameraPlayer
                onStreamOpen={onStreamOpen}
                streamingCamera={streamingCameraPlayback}
                streamType={STREAM_TYPE_PLAYBACK}
                ref={refCameraPlayer}
                onStreamClosed={onStreamClosed}
                muted={audioMuted}
                streamId={streamId}
                visible={true}
                onTransferTypeChange={setTransferType}
                playBackProps={{
                  startAt: streamStart * 1000,
                  endAt: streamEnd,
                  pause,
                  onPauseChange,
                  onProgressChange: handleProgressTime,
                  absoluteSeekTime: seekAt,
                }}
              >
                <div
                  className="absolute bottom-1 left-1 flex"
                  style={{ flexDirection: "column" }}
                >
                  <div className="mb-1  opacity-0 group-hover:opacity-100  rounded-lg px-2 py-1 text-xs text-center text-white bg-neutral-800 bg-opacity-75">
                    <SvgMapMarker
                      className={clsx("inline-block fill-zinc-300 mr-1 ")}
                      width="12"
                    />
                    <span>{streamingCameraPlayback?.siteName || ""}</span>
                  </div>
                  <div
                    className=" rounded-lg px-2 py-1 text-xs text-center text-white bg-neutral-800 bg-opacity-75"
                    style={{ width: "fit-content" }}
                  >
                    <span
                      className={clsx(
                        "inline-block w-2 h-2 rounded-full mr-1",
                        streamingCameraPlayback?.status == "online"
                          ? "bg-emerald-400"
                          : "bg-red-500",
                      )}
                    ></span>
                    {streamingCameraPlayback?.cameraName}
                    {transferType === "turn" && (
                      <SvgCloudTransfer
                        width={18}
                        style={{ display: "inline-block", marginLeft: "6px" }}
                      />
                    )}
                    {transferType === "other" && (
                      <SvgLocalTransfer
                        width={18}
                        style={{ display: "inline-block", marginLeft: "6px" }}
                      />
                    )}
                    {transferType === "p2p" && (
                      <SvgLocalTransfer
                        width={18}
                        style={{ display: "inline-block", marginLeft: "6px" }}
                      />
                    )}
                  </div>
                </div>
              </UsingCameraPlayer>
            </div>
          </div>
        ) : (
          <div className="aspect-video bg-zinc-100 w-full rounded-2xl border-4 border-transparent"></div>
        )}
      </div>
      {/* Play bar */}
      <Timeline
        orgId={streamingCameraPlayback?.orgId}
        disableSwitchArrow={disableSwitchArrow}
        cameraId={+streamingCameraPlayback?.cameraId}
        timelineInterval={timelineInterval}
        startAtTimeline={startAtTimeline}
        endAtTimeline={endAtTimeline}
        setStartAtTimeline={setStartAtTimeline}
        setEndAtTimeline={setEndAtTimeline}
        playBackStreamId={streamId}
        capabilities={streamingCameraPlayback?.capabilities}
        disableAlertLine={disableAlertLine}
        onSeekAt={(seek) => setSeekAt(seek)}
        progressTime={progressTime}
      />
    </>
  );
});
